/**
 * V227: 권한/리전 진단 패널
 *
 * 개발환경(localhost)에서만 표시되는 디버깅 페이지
 * - 현재 로그인 상태, uid, email
 * - Firebase projectId, Functions region
 * - debugMyRoles 호출 결과
 */

import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebase/config';
import { onAuthStateChanged, User } from 'firebase/auth';

// 개발환경 체크
const isDev = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

interface DebugResult {
  uid: string;
  email: string | null;
  roles: string[];
  isAdminLike: boolean;
  hasRoleExec: boolean;
  hasMmAccess: boolean;
  time: string;
}

interface DebugError {
  code: string;
  message: string;
}

export default function DebugPanelPage() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [result, setResult] = useState<DebugResult | null>(null);
  const [error, setError] = useState<DebugError | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Auth 상태 구독
  useEffect(() => {
    if (!auth) {
      setAuthLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 개발환경 아니면 접근 차단
  if (!isDev) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h1>404 Not Found</h1>
      </div>
    );
  }

  const handleRunDebug = async () => {
    if (!functions) {
      setError({ code: 'no-functions', message: 'Firebase Functions가 초기화되지 않았습니다.' });
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const debugMyRoles = httpsCallable<object, DebugResult>(functions, 'debugMyRoles');
      const response = await debugMyRoles({});
      setResult(response.data);
    } catch (err: any) {
      setError({
        code: err.code || 'unknown',
        message: err.message || String(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    const data = result || error;
    if (data) {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || '(not set)';
  const functionsRegion = 'asia-northeast3'; // config.ts:60 기준

  return (
    <div style={{ padding: 20, fontFamily: 'monospace', maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ borderBottom: '2px solid #333', paddingBottom: 10 }}>
        V227 Debug Panel
      </h1>

      <p style={{ color: '#666', fontSize: 12 }}>
        이 패널은 localhost 개발환경에서만 표시됩니다.
      </p>

      {/* 섹션 1: Firebase 설정 */}
      <section style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
        <h2 style={{ margin: '0 0 12px 0', fontSize: 16 }}>1. Firebase 설정</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ padding: 4, fontWeight: 'bold' }}>projectId:</td>
              <td style={{ padding: 4 }}>{projectId}</td>
            </tr>
            <tr>
              <td style={{ padding: 4, fontWeight: 'bold' }}>Functions region:</td>
              <td style={{ padding: 4 }}>
                <span style={{ color: 'green' }}>{functionsRegion}</span>
                {' '}(config.ts:60)
              </td>
            </tr>
            <tr>
              <td style={{ padding: 4, fontWeight: 'bold' }}>Functions 초기화:</td>
              <td style={{ padding: 4 }}>
                {functions ? '✅ OK' : '❌ NULL'}
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* 섹션 2: 로그인 상태 */}
      <section style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
        <h2 style={{ margin: '0 0 12px 0', fontSize: 16 }}>2. 로그인 상태</h2>
        {authLoading ? (
          <p>로딩 중...</p>
        ) : !auth ? (
          <p style={{ color: 'red' }}>❌ Firebase Auth 초기화 안 됨</p>
        ) : !user ? (
          <p style={{ color: 'orange' }}>⚠️ 로그인 안 됨</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr>
                <td style={{ padding: 4, fontWeight: 'bold' }}>currentUser:</td>
                <td style={{ padding: 4, color: 'green' }}>✅ 존재</td>
              </tr>
              <tr>
                <td style={{ padding: 4, fontWeight: 'bold' }}>uid:</td>
                <td style={{ padding: 4 }}>{user.uid}</td>
              </tr>
              <tr>
                <td style={{ padding: 4, fontWeight: 'bold' }}>email:</td>
                <td style={{ padding: 4 }}>{user.email}</td>
              </tr>
            </tbody>
          </table>
        )}
      </section>

      {/* 섹션 3: debugMyRoles 호출 */}
      <section style={{ marginBottom: 24, padding: 16, background: '#e8f4e8', borderRadius: 8 }}>
        <h2 style={{ margin: '0 0 12px 0', fontSize: 16 }}>3. debugMyRoles 호출</h2>

        <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
          Network 탭에서 요청 URL 확인: <code>https://asia-northeast3-ngle-works.cloudfunctions.net/debugMyRoles</code>
        </p>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <button
            onClick={handleRunDebug}
            disabled={loading || !user}
            style={{
              padding: '10px 20px',
              fontSize: 14,
              background: loading ? '#999' : '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: loading || !user ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Loading...' : 'Run debugMyRoles'}
          </button>

          <button
            onClick={handleCopy}
            disabled={!result && !error}
            style={{
              padding: '10px 20px',
              fontSize: 14,
              background: copied ? '#4CAF50' : '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: !result && !error ? 'not-allowed' : 'pointer',
            }}
          >
            {copied ? 'Copied!' : 'Copy JSON'}
          </button>
        </div>

        {/* 결과 표시 */}
        {error && (
          <div style={{ background: '#ffebee', padding: 12, borderRadius: 4, marginBottom: 12 }}>
            <strong style={{ color: '#c62828' }}>Error:</strong>
            <pre style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap', color: '#c62828' }}>
              {JSON.stringify(error, null, 2)}
            </pre>
          </div>
        )}

        {result && (
          <div style={{ background: '#e8f5e9', padding: 12, borderRadius: 4 }}>
            <strong style={{ color: '#2e7d32' }}>Result:</strong>
            <pre style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(result, null, 2)}
            </pre>

            {/* 빠른 판정 */}
            <div style={{ marginTop: 16, padding: 12, background: '#fff', borderRadius: 4 }}>
              <strong>빠른 판정:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                <li>
                  roles: {result.roles.length === 0 ? (
                    <span style={{ color: 'red' }}>❌ 빈 배열 (역할 미할당)</span>
                  ) : (
                    <span style={{ color: 'green' }}>✅ {result.roles.join(', ')}</span>
                  )}
                </li>
                <li>
                  isAdminLike: {result.isAdminLike ? (
                    <span style={{ color: 'green' }}>✅ true</span>
                  ) : (
                    <span style={{ color: 'orange' }}>⚠️ false</span>
                  )}
                </li>
                <li>
                  hasMmAccess: {result.hasMmAccess ? (
                    <span style={{ color: 'green' }}>✅ true (MM 마스터 접근 가능)</span>
                  ) : (
                    <span style={{ color: 'red' }}>❌ false (403 발생 원인)</span>
                  )}
                </li>
              </ul>
            </div>
          </div>
        )}
      </section>

      {/* 섹션 4: 판정 가이드 */}
      <section style={{ padding: 16, background: '#fff3e0', borderRadius: 8 }}>
        <h2 style={{ margin: '0 0 12px 0', fontSize: 16 }}>4. 결과 해석 가이드</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#ffe0b2' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>roles</th>
              <th style={{ padding: 8, textAlign: 'left' }}>isAdminLike</th>
              <th style={{ padding: 8, textAlign: 'left' }}>판정</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: 8 }}>[]</td>
              <td style={{ padding: 8 }}>false</td>
              <td style={{ padding: 8 }}>역할 미할당</td>
            </tr>
            <tr style={{ background: '#fafafa' }}>
              <td style={{ padding: 8 }}>["role_manager"]</td>
              <td style={{ padding: 8 }}>false</td>
              <td style={{ padding: 8 }}>MM 권한 없는 역할</td>
            </tr>
            <tr>
              <td style={{ padding: 8 }}>["system_admin"]</td>
              <td style={{ padding: 8 }}>false</td>
              <td style={{ padding: 8, color: 'red' }}>⚠️ 코드 버그</td>
            </tr>
            <tr style={{ background: '#fafafa' }}>
              <td style={{ padding: 8 }}>["system_admin"]</td>
              <td style={{ padding: 8 }}>true</td>
              <td style={{ padding: 8, color: 'green' }}>정상</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  );
}
